/**
 * Single source of truth for the BIOS brand palette.
 *
 * Editorial dual-palette: warm cream paper by day, warm graphite at night.
 * Both sides share the same hue family so the brand reads as one publication,
 * just at different times of day.
 *
 *                     light                   dark
 *   paper / page bg   #f6f2ea (cream)         #11100e (warm graphite)
 *   surface (cards)   #fffdf9 (warm white)    #1a1815 (lifted graphite)
 *   surface-2         #ebe6df (cream-2)       #23201c (2nd elevation)
 *   display text      #14110f (ink)           #f6f2ea (cream — mirror)
 *   body              #3d3632                 #d8cfc4
 *   muted             #5c534c                 #a39689
 *   subtle            #7a7068                 #7a6f64
 *   accent            #63659e (dusty purple)  #a0a2d8 (lifted dusty purple)
 *
 * NAMESPACE CONVENTION (Tailwind utilities):
 *   • Light mode uses the `figures-*` tokens: text-figures-ink / -body / -muted
 *     / -subtle, bg-figures-paper / -wash, border-figures-rule.
 *   • Dark mode uses the `brand-*Inv` tokens via the `dark:` variant:
 *     dark:text-brand-displayInv / -bodyInv / -mutedInv / -subtleInv.
 *   • The `brand-*` base names (surface, surfaceLow, bg) exist only for dark
 *     chrome that has no light counterpart. Don't reach for `brand-display` /
 *     `-body` / `-muted` in light mode — that's what `figures-*` is for.
 * `figures.X` and `brand.X` (base) hold identical light values, so the two are
 * interchangeable at runtime; the split above is purely for legibility.
 *
 * Consumed by tailwind.config.js (utility tokens via require), src/theme.js
 * (the MUI palette), and the handful of components that need raw color values
 * (dark tooltips, the coverage/confidence scale). ESM on purpose: Vite serves
 * src files as ESM with no CJS interop, and the Tailwind config's require()
 * still works because Tailwind loads its config through jiti, which
 * transpiles these named exports.
 *
 * The CSS layer (index.css, App.css, UploadModel.css, ErrorBoundary.css) can't
 * import JS, so it reads these values as CSS custom properties from
 * src/tokens.css — generated from this file by `node scripts/generate-tokens.mjs`
 * and guarded against drift by src/tokens.test.js. After changing a value here,
 * regenerate tokens.css.
 */

const figures = {
  paper: "#f6f2ea",
  ink: "#14110f",
  // Titles/headings — near-black ink in light mode (dark mode keeps cream via
  // `dark:` overrides on each usage).
  heading: "#14110f",
  body: "#3d3632", // light body text (dark counterpart: brand.bodyInv)
  muted: "#5c534c",
  subtle: "#7a7068", // faintest light text (dark counterpart: brand.subtleInv)
  accent: "#63659e",
  rule: "rgba(20, 17, 15, 0.14)",
  wash: "#fffdf9",
  // Light 2nd-elevation card surface (cream-2). Dark counterpart: brand.surfaceLow.
  wash2: "#ebe6df",
};

const brand = {
  paper: "#f6f2ea",
  surface: "#1a1815",
  surfaceLow: "#23201c",
  bg: "#11100e",
  display: "#14110f",
  displayInv: "#f6f2ea",
  body: "#3d3632",
  bodyInv: "#d8cfc4",
  muted: "#5c534c",
  mutedInv: "#a39689",
  subtle: "#7a7068",
  subtleInv: "#7a6f64",
  accent: "#63659e",
  accentInv: "#a0a2d8",
};

/**
 * Coverage / confidence "traffic light" scale. Shared by the Align-Standards
 * coverage bands (full / partial / none) and the Analyze-Document confidence
 * colors, which use the same three tones.
 */
const status = {
  full: "#7f9b5e", // green — fully addressed / high confidence
  partial: "#c6a44e", // amber — partially addressed / medium confidence
  none: "#cf8a8a", // dusty red — gap / low confidence
};

/**
 * Build an `rgba()` string from a #rrggbb token and an alpha, so brand colors
 * used at partial opacity (scrims, hairlines, dividers) still trace back here
 * instead of being hand-typed as rgba literals. e.g. alpha(brand.bg, 0.55).
 */
const alpha = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

export { figures, brand, status, alpha };
